import type { FieldType } from '@lighthouse/core'
import { getFieldIcon, IGNORE_WATCH_NODE_FIELD_TYPES, type UpdateRecordTriggerConfig } from '@lighthouse/shared'
import { find } from 'rambda'
import React, { useMemo } from 'react'
import useSwr from 'swr'

import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSource, useDataSourceOptions } from '@/hooks/useDataSource'
import * as srv from '@/services'

import * as CM from '../styles'
import * as SC from './styles'

type UpdateRecordTriggerCardProps = {
    config: UpdateRecordTriggerConfig
}

interface fieldList {
    label: string
    value: string
    type: FieldType
    icon: string
}

const UpdateRecordTriggerCard: React.FC<UpdateRecordTriggerCardProps> = ({ config }) => {
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSource = useDataSource(appId, envId, config.dataSourceId)
    const { data: listDs } = useSwr(`/fetch/listDs/${appId}`, () => srv.listDs(appId, envId), {
        revalidateOnFocus: false
    })
    const options = useDataSourceOptions()

    const option = useMemo(() => {
        const option = options.find(item => item.value === config.dataSourceId)
        return { ...option, icon: <SC.Icon type={option?.icon ?? ''} /> }
    }, [config.dataSourceId, options])

    const fieldsList = useMemo(() => {
        if (!dataSource) {
            return []
        }
        const {
            schema,
            viewOptions: { tableProps }
        } = dataSource

        const newTableProps = tableProps.reduce<fieldList[]>((list, cur) => {
            const field = schema[cur.id]
            if (field && !IGNORE_WATCH_NODE_FIELD_TYPES.has(field.type)) {
                list.push({
                    label: field.name,
                    value: field.id,
                    type: field.type,
                    icon: getFieldIcon(field.id, field.type, field.innerType)
                })
            }
            return list
        }, [])

        return Object.values(schema).reduce((prev, cur) => {
            const field = find(item => item.value === cur.id, prev)
            if (!field && !IGNORE_WATCH_NODE_FIELD_TYPES.has(cur.type)) {
                prev.push({
                    label: cur.name,
                    value: cur.id,
                    type: cur.type,
                    icon: getFieldIcon(cur.id, cur.type, cur.innerType)
                })
            }
            return prev
        }, newTableProps)
    }, [dataSource])

    const activeFieldList = useMemo(() => {
        if (config?.watchedFields) {
            return fieldsList.filter(item => config.watchedFields.includes(item.value))
        }
        return []
    }, [config?.watchedFields, fieldsList])

    if (!option) {
        return null
    }

    return (
        <CM.CardItemContainer>
            <SC.TypeName>当记录被更新时</SC.TypeName>

            <CM.FieldList>
                <CM.FieldItem>
                    <CM.FieldItemLeftFill>
                        {option?.icon}
                        <CM.FieldItemTitle>{option?.label}</CM.FieldItemTitle>
                    </CM.FieldItemLeftFill>
                </CM.FieldItem>
            </CM.FieldList>

            {activeFieldList.length > 0 && <SC.TypeName>关注以下任一字段的变更</SC.TypeName>}
            <CM.FieldList>
                {activeFieldList.map(item => (
                    <CM.FieldItem key={item.value}>
                        <CM.FieldItemLeftFill>
                            <SC.Icon type={item.icon} />
                            <CM.FieldItemTitle>{item.label}</CM.FieldItemTitle>
                        </CM.FieldItemLeftFill>
                    </CM.FieldItem>
                ))}
            </CM.FieldList>
        </CM.CardItemContainer>
    )
}

export default UpdateRecordTriggerCard
